import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  CircularProgress,
  TablePagination,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import RefreshIcon from '@material-ui/icons/Refresh';
import GetAppIcon from '@material-ui/icons/GetApp';
import ReceiptIcon from '@material-ui/icons/Receipt';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import Alert from '@material-ui/lab/Alert';
import { useNavigate } from 'react-router-dom';
import transactionService from '../../services/transactionService';

import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import StatusChip from '../../components/ui/StatusChip';
import SearchBar from '../../components/ui/SearchBar';
import StatCard from '../../components/ui/StatCard';
import { exportToCsv } from '../../utils/exportCsv';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  search: { maxWidth: 300, flexGrow: 1 },
  count: { color: theme.palette.text.secondary, fontSize: '0.85rem' },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 280,
  },
  reference: { fontWeight: 700, fontFamily: 'monospace', fontSize: '0.82rem' },
  amount: { fontWeight: 700 },
  amountIn: { color: '#047857' },
  amountOut: { color: '#b91c1c' },
}));

const STATUS_NORM = {
  completed: 'paid',
  Completed: 'paid',
  pending: 'pending',
  Pending: 'pending',
  failed: 'failed',
  Failed: 'failed',
};

const STATUS_LABEL = {
  paid: 'Terminée',
  pending: 'En attente',
  failed: 'Échouée',
};

const TYPE_LABEL = {
  payment: 'Paiement',
  refund: 'Remboursement',
  commission_payment: 'Commission',
};

const PAYMENT_LABEL = {
  card: 'Carte',
  paypal: 'PayPal',
  bank_transfer: 'Virement',
  mobile_money: 'Mobile Money',
};

const formatDate = (val) => {
  if (!val) return '—';
  const d = val.toDate ? val.toDate() : new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatCurrency = (amount, currency = 'XOF') => {
  if (amount == null) return '—';
  if (currency === 'EUR') {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  }
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0 }).format(amount) + ' FCFA';
};

const TransactionsListPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const list = (await transactionService.getAllTransactions()) || [];
      setTransactions(list);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la récupération des transactions', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const summary = useMemo(() => {
    const now = new Date();
    let totalIncome = 0;
    let totalExpenses = 0;
    let pendingAmount = 0;
    let thisMonthAmount = 0;
    transactions.forEach((t) => {
      const amount = t.amount || 0;
      const norm = STATUS_NORM[t.status] || 'pending';
      const dt = t.createdAt?.toDate ? t.createdAt.toDate() : new Date(t.createdAt);
      const sameMonth =
        dt && !Number.isNaN(dt.getTime()) &&
        dt.getMonth() === now.getMonth() &&
        dt.getFullYear() === now.getFullYear();

      if (norm === 'paid') {
        if (t.type === 'payment') {
          totalIncome += amount;
          if (sameMonth) thisMonthAmount += amount;
        } else if (t.type === 'refund' || t.type === 'commission_payment') {
          totalExpenses += amount;
          if (sameMonth) thisMonthAmount -= amount;
        }
      } else if (norm === 'pending') {
        pendingAmount += amount;
      }
    });
    return { totalIncome, totalExpenses, pendingAmount, thisMonthAmount };
  }, [transactions]);

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return transactions.filter((t) => {
      const matchSearch =
        (t.reference || '').toLowerCase().includes(q) ||
        (t.id || '').toLowerCase().includes(q) ||
        (t.userName || '').toLowerCase().includes(q) ||
        (t.partnerName || '').toLowerCase().includes(q) ||
        (t.bookingReference || '').toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q);
      const matchType = typeFilter === 'all' || t.type === typeFilter;
      const norm = STATUS_NORM[t.status] || 'pending';
      const matchStatus = statusFilter === 'all' || norm === statusFilter;

      let matchPeriod = true;
      if (periodFilter !== 'all') {
        const dt = t.createdAt?.toDate ? t.createdAt.toDate() : new Date(t.createdAt);
        if (dt && !Number.isNaN(dt.getTime())) {
          const now = new Date();
          if (periodFilter === 'this_month') {
            matchPeriod = dt.getMonth() === now.getMonth() && dt.getFullYear() === now.getFullYear();
          } else if (periodFilter === 'last_month') {
            const lm = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
            const lmy = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
            matchPeriod = dt.getMonth() === lm && dt.getFullYear() === lmy;
          } else if (periodFilter === 'this_year') {
            matchPeriod = dt.getFullYear() === now.getFullYear();
          }
        } else {
          matchPeriod = false;
        }
      }

      return matchSearch && matchType && matchStatus && matchPeriod;
    });
  }, [transactions, searchTerm, typeFilter, statusFilter, periodFilter]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleDeleteConfirm = async () => {
    if (!transactionToDelete) return;
    try {
      await transactionService.deleteTransaction(transactionToDelete.id);
      setTransactions((prev) => prev.filter((t) => t.id !== transactionToDelete.id));
      setSnackbar({ open: true, message: 'Transaction supprimée', severity: 'success' });
    } catch (err) {
      console.error('Error deleting transaction:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la suppression', severity: 'error' });
    } finally {
      setOpenDeleteDialog(false);
      setTransactionToDelete(null);
    }
  };

  const handleExport = () => {
    exportToCsv(
      filtered.map((t) => ({
        ...t,
        type: TYPE_LABEL[t.type] || t.type,
        paymentMethod: PAYMENT_LABEL[t.paymentMethod] || t.paymentMethod,
        status: STATUS_LABEL[STATUS_NORM[t.status] || 'pending'],
        createdAt: formatDate(t.createdAt),
      })),
      ['reference', 'type', 'createdAt', 'userName', 'paymentMethod', 'amount', 'status'],
      {
        reference: 'Référence',
        type: 'Type',
        createdAt: 'Date',
        userName: 'Client',
        paymentMethod: 'Méthode',
        amount: 'Montant',
        status: 'Statut',
      },
      'transactions'
    );
  };

  return (
    <Box className="fade-in-up">
      <PageHeader
        breadcrumbs={[{ label: 'Admin', to: '/admin' }, { label: 'Transactions' }]}
        title="Transactions"
        subtitle={`${filtered.length} transaction${filtered.length > 1 ? 's' : ''}`}
        actions={
          <>
            <Tooltip title="Exporter CSV">
              <IconButton onClick={handleExport} style={{ background: '#fff', border: '1px solid rgba(17,24,39,0.08)' }}>
                <GetAppIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Rafraîchir">
              <IconButton onClick={fetchTransactions} style={{ background: '#fff', border: '1px solid rgba(17,24,39,0.08)' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/transactions/create')}
            >
              Nouvelle transaction
            </Button>
          </>
        }
      />

      {/* Summary cards */}
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TrendingUpIcon />}
            label="Total des revenus"
            value={formatCurrency(summary.totalIncome)}
            gradient="linear-gradient(135deg, #10b981, #059669)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TrendingDownIcon />}
            label="Total des dépenses"
            value={formatCurrency(summary.totalExpenses)}
            gradient="linear-gradient(135deg, #ef4444, #b91c1c)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<HourglassEmptyIcon />}
            label="En attente"
            value={formatCurrency(summary.pendingAmount)}
            gradient="linear-gradient(135deg, #f59e0b, #d97706)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<AccountBalanceIcon />}
            label="Bilan ce mois-ci"
            value={formatCurrency(summary.thisMonthAmount)}
            gradient="linear-gradient(135deg, #6366f1, #4f46e5)"
          />
        </Grid>
      </Grid>

      <Paper>
        <Box className={classes.toolbar}>
          <Box className={classes.search}>
            <SearchBar
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0); }}
              placeholder="Référence, client, description…"
            />
          </Box>
          <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="payment">Paiement</MenuItem>
              <MenuItem value="refund">Remboursement</MenuItem>
              <MenuItem value="commission_payment">Commission</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              label="Statut"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="paid">Terminée</MenuItem>
              <MenuItem value="pending">En attente</MenuItem>
              <MenuItem value="failed">Échouée</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Période</InputLabel>
            <Select
              value={periodFilter}
              label="Période"
              onChange={(e) => { setPeriodFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">Toutes</MenuItem>
              <MenuItem value="this_month">Ce mois-ci</MenuItem>
              <MenuItem value="last_month">Mois dernier</MenuItem>
              <MenuItem value="this_year">Cette année</MenuItem>
            </Select>
          </FormControl>
          <Typography className={classes.count}>
            {filtered.length} résultat{filtered.length > 1 ? 's' : ''}
          </Typography>
        </Box>

        {loading ? (
          <Box className={classes.loading}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<ReceiptIcon style={{ fontSize: 36 }} />}
            title="Aucune transaction"
            description={
              searchTerm || typeFilter !== 'all' || statusFilter !== 'all' || periodFilter !== 'all'
                ? 'Aucune transaction ne correspond à vos filtres.'
                : "Il n'y a pas encore de transactions."
            }
          />
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Référence</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Méthode</TableCell>
                    <TableCell align="right">Montant</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((t) => {
                    const norm = STATUS_NORM[t.status] || 'pending';
                    const isOut = t.type === 'refund' || t.type === 'commission_payment';
                    return (
                      <TableRow key={t.id} hover>
                        <TableCell>
                          <Box className={classes.reference}>{t.reference || t.id}</Box>
                          {t.bookingReference && (
                            <Box style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                              {t.bookingReference}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>{TYPE_LABEL[t.type] || t.type}</TableCell>
                        <TableCell style={{ whiteSpace: 'nowrap' }}>{formatDate(t.createdAt)}</TableCell>
                        <TableCell>{t.userName || t.partnerName || '—'}</TableCell>
                        <TableCell>
                          {PAYMENT_LABEL[t.paymentMethod] || t.paymentMethod || '—'}
                          {t.cardLast4 && (
                            <Box style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                              **** {t.cardLast4}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            className={`${classes.amount} ${isOut ? classes.amountOut : classes.amountIn}`}
                          >
                            {isOut ? '-' : '+'}
                            {formatCurrency(t.amount, t.currency)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <StatusChip status={norm} label={STATUS_LABEL[norm]} />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Voir">
                            <IconButton size="small" onClick={() => navigate(`/admin/transactions/${t.id}`)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Modifier">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/admin/transactions/${t.id}/edit`)}
                                disabled={norm === 'paid'}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Supprimer">
                            <span>
                              <IconButton
                                size="small"
                                style={{ color: norm === 'paid' ? undefined : '#ef4444' }}
                                onClick={() => { setTransactionToDelete(t); setOpenDeleteDialog(true); }}
                                disabled={norm === 'paid'}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Par page"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
            />
          </>
        )}
      </Paper>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Supprimer la transaction <strong>{transactionToDelete?.reference}</strong> ?
            Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleDeleteConfirm}
            style={{ background: '#ef4444', color: '#fff' }}
            autoFocus
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TransactionsListPage;
